import { lazy, Suspense, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, SUBSCRIPTIONS_PATH, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '~/routes/use-on-load';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import useGoto from '~/hooks/use-goto';
import useSubscriptionsEnabled from '~/hooks/use-subscriptions-enabled';
import { clearPendingSubscriptionPlan } from '~/hooks/use-pending-subscription-plan';
import { useEntitlements } from '~/hooks/use-entitlements';
import CheckoutFrame, { CheckoutHostMessage } from '~/components/checkout-embed/checkout-frame';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { P2GBox } from '@parcel2go/core-ui';

const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

export default function Component() {
  const { track } = useSmartSendAnalytics();
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { dispatch: contentDispatch } = useContext(ContentContext);
  const { t } = useTranslation(TranslationNamespaces.MYACCOUNT);
  const title = t('MY_PLAN_PAGE_TITLE', { defaultValue: 'My Plan' });
  const { goTo } = useGoto();
  const subscriptionsEnabled = useSubscriptionsEnabled();
  const { sync } = useEntitlements();

  // The plans page posts this one message additively (docs/EMBEDDED_PAYMENT.md) once a subscribe or
  // swap completes - the sidebar plan widget reads the same entitlements cache this refreshes.
  const handleMessage = useCallback(
    (message: CheckoutHostMessage) => {
      if (message.type === 'subscription:complete') {
        void sync();
      }
    },
    [sync]
  );

  // Direct navigation (a bookmark, the back button, a stale link) can reach this route even once
  // the account tab hides it for a tenant with no subscriptions - bounce to Orders rather than
  // embed a subscriptions page the tenant doesn't sell.
  useEffect(() => {
    if (!subscriptionsEnabled) {
      goTo(RoutingConstants.OrderHistory, { navigateOptions: { replace: true } });
    }
  }, [subscriptionsEnabled, goTo]);

  useOnLoad(() => {
    if (!subscriptionsEnabled) return;
    // A plan captured earlier - from a tier link, or carried across from onboarding - is spent as
    // soon as they open this page. My Plan always shows the full set of plans, so there is nothing
    // here for a stored plan to do, and leaving it would follow them into a later, unrelated visit.
    clearPendingSubscriptionPlan();
    document.title = title;
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.MyPlan }
    });
    contentDispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.SUBSCRIPTIONS });
    track({
      type: 'page_view',
      payload: {
        ...GenerateBaseAnalyticsPayload([]),
        page: RoutingConstants.MyPlan
      }
    });
  });

  if (!subscriptionsEnabled) {
    return null;
  }

  return (
    <Tagged tag="screen-my-plan">
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CheckoutFrame path={SUBSCRIPTIONS_PATH} title={title} minHeight="100px" onMessage={handleMessage} />
        {/* Ours, not the frame's - outside the iframe, so it changes without a checkout release and
          cannot be clipped by the frame's height. */}
        <Suspense>
          <LazyCmsContent contentKey="content-subscription-faqs" />
        </Suspense>
      </P2GBox>
    </Tagged>
  );
}

Component.displayName = 'My Plan';
